import { InfiniteData, UseInfiniteQueryResult } from "@tanstack/react-query";

import { ReactNode, useState } from "react";

import {
  CustomerZoneListResponseType,
  StorefrontPreviewProductListResponseType,
  StorefrontPreviewCustomerListResponseType,
  StorefrontPreviewCustomerZoneListResponseType,
  StorefrontPreviewOrderRulesListResponseType,
} from "@sellar-io/api-v2/types";

import { useSellarCurrentBusiness } from "../../hooks/useSellarCurrentBusiness.shared";

import { useDebounceValue } from "usehooks-ts";

import { useStorefrontPreviewCustomerZoneList } from "../../hooks/api/v2/storefrontPreview/useStorefrontPreviewCustomerZoneList";

import { useStorefrontPreviewOrderRulesList } from "../../hooks/api/v2/storefrontPreview/useStorefrontPreviewOrderRulesList";

import { useStorefrontPreviewCustomerList } from "../../hooks/api/v2/storefrontPreview/useStorefrontPreviewCustomerList";

import { useStorefrontPreviewProductList } from "../../hooks/api/v2/storefrontPreview/useStorefrontPreviewProductList";

import { useRemoteFeatureFlags } from "../../hooks/useRemoteFeatureFlags";
import { useProductCatalogueMode } from "../../hooks/useProductCatalogueMode";
import { useAvailabilityTierList } from "../../hooks/api/v2/availabilityTier/useAvailabilityTierList";

import { useNavigate } from "react-router";

import { ORDER_BUYER } from "../../constants/routes";

export type CustomerZone = Omit<
  StorefrontPreviewCustomerZoneListResponseType[number],
  "SupplierId" | "VolumeDiscountsId"
>;

export type BaseSelection = {
  availabilityTierId: number | null;
  priceTierId: number | null;
  orderRulesId: number | null;
};

export type SelectedCustomer = BaseSelection & {
  connectionId: number | null;
};

export type SelectedCustomerZone = BaseSelection & {
  customerZoneId: number | null;
};

export type Customer = StorefrontPreviewCustomerListResponseType[number];

export type StorefrontPreviewerProps = {
  trigger: ReactNode;
  open: boolean;
  initialTab: StorefrontPreviewerTabs;
  businessName: string;
  customerZoneSearchProps: {
    customerZones: CustomerZone[];
    isSearchingForCustomerZone: boolean;
    selectedCustomerZone: SelectedCustomerZone | null;
    onSearchForCustomerZone: (searchTerm: string) => void;
    onSelectCustomerZone: (params: SelectedCustomerZone | null) => void;
  };
  customerSearchProps: {
    customers: Customer[];
    isSearchingForCustomer: boolean;
    selectedCustomer: SelectedCustomer | null;
    onSearchForCustomer: (searchTerm: string) => void;
    onSelectCustomer: (params: SelectedCustomer | null) => void;
  };
  productListQuery: UseInfiniteQueryResult<
    InfiniteData<StorefrontPreviewProductListResponseType, unknown>
  >;
  orderRule: {
    name: string;
    minimumOrderQuantities: Array<
      StorefrontPreviewOrderRulesListResponseType["PriceTierOrderRules"][number]
    >;
    minimumOrderValue: number;
  };

  featureFlags: {
    isOrderRulesEnabled: boolean;
  };
  onTogglePreviewer: (open: boolean) => void;
  onChangeTab: (tab: StorefrontPreviewerTabs) => void;
  onClickStorefrontPreview: () => void;
};

export type OrderRulesInformationProps = StorefrontPreviewerProps;

export type ProductListProps = StorefrontPreviewerProps;

export type CustomColumn<T> = {
  id: string;
  key: keyof T;
  header: ReactNode;
};

export type CustomGridTableProps<T> = {
  columns: Array<CustomColumn<T>>;
  rows: T[];
  className: string;
};

export enum StorefrontPreviewerTabs {
  CUSTOMER,
  CUSTOMER_ZONE,
}

export const tabOptions = [
  { tab: StorefrontPreviewerTabs.CUSTOMER, label: "Customer" },
  { tab: StorefrontPreviewerTabs.CUSTOMER_ZONE, label: "Customer zone" },
];

export const isThirdParty = (customerZone: {
  type: CustomerZoneListResponseType[number]["type"];
}) => {
  return customerZone.type === "thirdParty";
};

export const useStorefrontPreviewerParameters = ({
  initialTab,
}: {
  initialTab: StorefrontPreviewerTabs;
}) => {
  const navigate = useNavigate();
  const [tab, setTab] = useState<StorefrontPreviewerTabs>(initialTab);
  const isOrderRulesEnabled = useRemoteFeatureFlags().isActive(
    "unbundlingRelease2A",
  );
  const productCatalogueMode = useProductCatalogueMode();
  const { business } = useSellarCurrentBusiness();
  const [openStorefrontPreviewer, setOpenStorefrontPreviewer] = useState(false);
  const [customerZoneSearchTerm, setCustomerZoneSearchTerm] = useState("");
  const [customerSearchTerm, setCustomerSearchTerm] = useState("");
  const [debouncedCustomerZoneSearchTerm] = useDebounceValue(
    customerZoneSearchTerm,
    500,
  );
  const [debouncedCustomerSearchTerm] = useDebounceValue(
    customerSearchTerm,
    500,
  );
  const [selectedCustomer, setSelectedCustomer] =
    useState<SelectedCustomer | null>(null);
  const [selectedCustomerZone, setSelectedCustomerZone] =
    useState<SelectedCustomerZone | null>(null);

  const storefrontPreviewCustomerZoneListQuery =
    useStorefrontPreviewCustomerZoneList({
      search: debouncedCustomerZoneSearchTerm,
      sortBy: "type",
      limit: 10,
    });

  const customerZones =
    storefrontPreviewCustomerZoneListQuery.data?.pages.flat() ?? [];

  const orderRulesId =
    tab === StorefrontPreviewerTabs.CUSTOMER
      ? selectedCustomer?.orderRulesId
      : selectedCustomerZone?.orderRulesId;

  const availabilityTierId =
    tab === StorefrontPreviewerTabs.CUSTOMER
      ? selectedCustomer?.availabilityTierId
      : selectedCustomerZone?.availabilityTierId;

  const availabilityTiersQuery = useAvailabilityTierList(
    { limit: 50 },
    { enabled: productCatalogueMode.isBasicMode },
  );
  const defaultAvailabilityTierId = availabilityTiersQuery.data?.pages
    .flat()
    .find((availabilityTier) => availabilityTier.defaultType === "default")?.id;
  const storefrontAvailabilityTierId = productCatalogueMode.isBasicMode
    ? defaultAvailabilityTierId
    : availabilityTierId;

  const priceTierId =
    tab === StorefrontPreviewerTabs.CUSTOMER
      ? selectedCustomer?.priceTierId
      : selectedCustomerZone?.priceTierId;

  const storefrontPreviewProductListQuery = useStorefrontPreviewProductList(
    {
      query: [
        {
          type: "availabilityTierId",
          modifier: "EQUALS",
          value: storefrontAvailabilityTierId ?? 0,
        },
        {
          type: "priceTierId",
          modifier: "EQUALS",
          value: priceTierId ?? 0,
        },
      ],
    },
    {
      enabled: Boolean(storefrontAvailabilityTierId),
    },
  );

  const storefrontPreviewOrderRulesListQuery =
    useStorefrontPreviewOrderRulesList(
      {
        query: [
          {
            type: "connectionId",
            modifier: "EQUALS",
            value: selectedCustomer?.connectionId ?? 0,
          },
          {
            type: "orderRulesId",
            modifier: "EQUALS",
            value: orderRulesId ?? 0,
          },
        ],
      },
      {
        enabled: Boolean(orderRulesId),
      },
    );

  const storefrontOrderRule =
    (storefrontPreviewOrderRulesListQuery.data?.pages.flat() ?? [])[0];

  const storefrontPreviewCustomerListQuery = useStorefrontPreviewCustomerList({
    search: debouncedCustomerSearchTerm,
    limit: 10,
  });

  const handleTogglePreviewer = (open: boolean) => {
    if (!open) {
      setSelectedCustomer(null);
      setSelectedCustomerZone(null);
      setCustomerSearchTerm("");
      setCustomerZoneSearchTerm("");
    }
    setOpenStorefrontPreviewer(open);
  };

  const handlePreviewStorefront = () => {
    const queryParameters = new URLSearchParams({
      ...(storefrontAvailabilityTierId && {
        availabilityTierId: `${storefrontAvailabilityTierId}`,
      }),
      ...(priceTierId && {
        priceTierId: `${priceTierId}`,
      }),
    });
    navigate(
      `${ORDER_BUYER.href({ supplierIdOrUrlId: supplierUrlId })}?${queryParameters.toString()}`,
    );
  };

  const prefillCustomerZone = async ({
    customerZone,
  }: {
    customerZone: CustomerZone;
  }) => {
    setCustomerZoneSearchTerm(customerZone.name ?? "");

    await storefrontPreviewCustomerZoneListQuery.refetch();

    setSelectedCustomerZone({
      availabilityTierId: customerZone.AvailabilityTierId,
      priceTierId: customerZone.PriceTierId,
      orderRulesId: customerZone.OrderRulesId,
      customerZoneId: customerZone.id,
    });
  };

  const prefillCustomer = async ({
    customer,
  }: {
    customer: Pick<
      Customer,
      "AvailabilityTier" | "PriceTier" | "OrderRules" | "id"
    > & {
      Retailer: {
        Business: {
          name: string;
        };
      };
    };
  }) => {
    setCustomerSearchTerm(customer.Retailer.Business.name);

    await storefrontPreviewCustomerListQuery.refetch();

    setSelectedCustomer({
      availabilityTierId: customer.AvailabilityTier?.id ?? null,
      priceTierId: customer.PriceTier?.id ?? null,
      orderRulesId: customer.OrderRules?.id ?? null,
      connectionId: customer.id,
    });
  };

  const isSearchingForCustomerZone =
    storefrontPreviewCustomerZoneListQuery.isLoading;
  const isSearchingForCustomer = storefrontPreviewCustomerListQuery.isLoading;
  const supplierUrlId = business?.Supplier?.urlId ?? "";
  const businessName = business?.name ?? business?.registeredCompanyName ?? "";
  const customers = storefrontPreviewCustomerListQuery.data?.pages.flat() ?? [];

  return {
    isSearchingForCustomer,
    isSearchingForCustomerZone,
    businessName,
    customers,
    customerZones,
    orderRule: storefrontOrderRule,
    productListQuery: storefrontPreviewProductListQuery,
    featureFlags: {
      isOrderRulesEnabled,
    },
    openStorefrontPreviewer,
    selectedCustomer,
    selectedCustomerZone,
    tab,
    setCustomerSearchTerm,
    setCustomerZoneSearchTerm,
    setSelectedCustomer,
    setSelectedCustomerZone,
    setOpenStorefrontPreviewer: handleTogglePreviewer,
    prefillCustomerZone,
    prefillCustomer,
    onChangeTab: setTab,
    onPreviewStorefront: handlePreviewStorefront,
  };
};
